import { useState, useMemo } from 'react';
import { TeamState, Player, CategoryAssignment, SetRecord, Timeout } from '@/types';
import { validateVolleyballCategories, resolveCategoryDependencies } from '@/lib/validation';
import ScoringHeader from '@/components/ScoringHeader';

type CategoryTab = 'D' | 'L' | 'B' | 'SF';

interface VolleyballCategoryModalProps {
  winningTeam: TeamState;
  losingTeam: TeamState;
  servingTeamId: string;
  currentServerId: string | null;
  servingTeam: TeamState | null;
  onConfirm: (categories: CategoryAssignment[], isServerFault?: boolean) => Promise<void>;
  onCancel: () => void;
  teamA: TeamState;
  teamB: TeamState;
  currentSetNumber: number;
  matchType: 'league' | 'playoff';
  currentScoreA: number;
  currentScoreB: number;
  completedSets: SetRecord[];
  completedTimeouts?: Timeout[];
}

export default function VolleyballCategoryModal({
  winningTeam,
  losingTeam,
  servingTeamId,
  currentServerId,
  servingTeam,
  onConfirm,
  onCancel,
  teamA,
  teamB,
  currentSetNumber,
  matchType,
  currentScoreA,
  currentScoreB,
  completedSets,
  completedTimeouts = [],
}: VolleyballCategoryModalProps) {
  const [aceServe, setAceServe] = useState(false);
  const [serverFault, setServerFault] = useState(false);
  const [defendersWinning, setDefendersWinning] = useState<Set<string>>(new Set());
  const [defendersLosing, setDefendersLosing] = useState<Set<string>>(new Set());
  const [liberoTeamA, setLiberoTeamA] = useState<string | null>(null);
  const [liberoTeamB, setLiberoTeamB] = useState<string | null>(null);
  const [setter, setSetter] = useState<string | null>(null);
  const [blocker, setBlocker] = useState<string | null>(null);
  const [finisher, setFinisher] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<CategoryTab>('D');

  const isWinningTeamServing = winningTeam.id === servingTeamId;
  const currentServer = isWinningTeamServing
    ? winningTeam.players.find(p => p.id === currentServerId) || null
    : losingTeam.players.find(p => p.id === currentServerId) || null;

  const canShowServerFault = !isWinningTeamServing && currentServerId;

  const servingTeamObj = servingTeam;

  const allDefenders = useMemo(() => new Set([...defendersWinning, ...defendersLosing]), [defendersWinning, defendersLosing]);
  const allLiberos = useMemo(() => {
    const s = new Set<string>();
    if (liberoTeamA) s.add(liberoTeamA);
    if (liberoTeamB) s.add(liberoTeamB);
    return s;
  }, [liberoTeamA, liberoTeamB]);

  const toggleDefender = (playerId: string, isWinningTeam: boolean) => {
    if (isWinningTeam) {
      const newSet = new Set(defendersWinning);
      if (newSet.has(playerId)) newSet.delete(playerId);
      else newSet.add(playerId);
      setDefendersWinning(newSet);
    } else {
      const newSet = new Set(defendersLosing);
      if (newSet.has(playerId)) newSet.delete(playerId);
      else newSet.add(playerId);
      setDefendersLosing(newSet);
    }
  };

  const handleLiberoSelect = (playerId: string, isWinningTeam: boolean) => {
    if (isWinningTeam) {
      setLiberoTeamA(liberoTeamA === playerId ? null : playerId);
    } else {
      setLiberoTeamB(liberoTeamB === playerId ? null : playerId);
    }
  };

  const handleBlockerSelect = (playerId: string | null) => {
    if (blocker === playerId) {
      setBlocker(null);
      if (finisher === playerId) setFinisher(null);
    } else {
      setSetter(null);
      setBlocker(playerId);
      setFinisher(playerId);
    }
  };

  const handleSetterSelect = (playerId: string | null) => {
    if (blocker) return;
    const next = playerId === setter ? null : playerId;
    setSetter(next);
    if (next && finisher === next) setFinisher(null);
    setError(null);
  };

  const handleFinisherSelect = (playerId: string | null) => {
    if (blocker && playerId !== blocker) {
      setError('When Blocker (B) is assigned, Finisher (F) must be the same player.');
      return;
    }
    const next = playerId === finisher ? null : playerId;
    setFinisher(next);
    if (next && setter === next) setSetter(null);
    setError(null);
  };

  const buildCategories = (): CategoryAssignment[] => {
    if (aceServe && currentServerId) {
      return [{ playerId: currentServerId, category: 'A' }];
    }

    let categories: CategoryAssignment[] = [];
    for (const pid of defendersWinning) categories.push({ playerId: pid, category: 'D' });
    for (const pid of defendersLosing) categories.push({ playerId: pid, category: 'D' });
    if (liberoTeamA) categories.push({ playerId: liberoTeamA, category: 'L' });
    if (liberoTeamB) categories.push({ playerId: liberoTeamB, category: 'L' });
    if (setter) categories.push({ playerId: setter, category: 'S' });
    if (blocker) categories.push({ playerId: blocker, category: 'B' });
    if (finisher) categories.push({ playerId: finisher, category: 'F' });
    categories = resolveCategoryDependencies(categories);
    return categories;
  };

  const handleConfirm = async () => {
    const categories = buildCategories();
    const validationError = validateVolleyballCategories(
      categories,
      winningTeam.id,
      losingTeam.id,
      servingTeamId,
      currentServerId,
      winningTeam.players,
      losingTeam.players
    );
    if (validationError) {
      setError(validationError.message);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(categories, serverFault);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save categories');
    } finally {
      setSubmitting(false);
    }
  };

  const setterNeedsFinisher = setter && !finisher && !blocker;

  // Player button styled like ServerSelectionModal (full size for B/S/F tabs)
  const PlayerButton = ({
    player,
    selected,
    teamColor,
    onClick,
    disabled,
  }: {
    player: Player;
    selected: boolean;
    teamColor: string;
    onClick: () => void;
    disabled?: boolean;
  }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`relative py-4 px-3 rounded-lg font-bold text-base transition-all min-h-[60px] ${
        disabled
          ? 'bg-gray-800 border-2 border-gray-700 text-gray-500 cursor-not-allowed'
          : selected
            ? 'text-white active:scale-[0.98]'
            : 'bg-surface-raised active:scale-[0.98] text-white'
      }`}
      style={
        disabled
          ? {}
          : selected
            ? { backgroundColor: teamColor, border: `4px solid ${teamColor}` }
            : { border: `4px solid ${teamColor}` }
      }
    >
      {player.name}
    </button>
  );

  // Compact player button for side-by-side D/L tabs (reduced height for 8 players)
  const CompactPlayerButton = ({
    player,
    selected,
    teamColor,
    onClick,
    disabled,
  }: {
    player: Player;
    selected: boolean;
    teamColor: string;
    onClick: () => void;
    disabled?: boolean;
  }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`relative py-2 px-2 rounded-lg font-semibold text-sm transition-all min-h-[40px] w-full ${
        disabled
          ? 'bg-gray-800 border-2 border-gray-700 text-gray-500 cursor-not-allowed'
          : selected
            ? 'text-white active:scale-[0.98]'
            : 'bg-surface-raised active:scale-[0.98] text-white'
      }`}
      style={
        disabled
          ? {}
          : selected
            ? { backgroundColor: teamColor, border: `4px solid ${teamColor}` }
            : { border: `4px solid ${teamColor}` }
      }
    >
      {player.name}
    </button>
  );

  // Summary component
  const SelectionSummary = () => {
    const hasSelections = defendersWinning.size > 0 || defendersLosing.size > 0 || liberoTeamA || liberoTeamB || setter || blocker || finisher;
    return (
      <div className="bg-surface rounded-lg p-2 border border-border">
        <p className="text-xs text-text-muted mb-1">Selected:</p>
        <div className="flex flex-wrap gap-1 text-xs min-h-[20px]">
          {Array.from(defendersWinning).map(pid => {
            const p = winningTeam.players.find(pl => pl.id === pid);
            return p ? (
              <span key={pid} className="px-1.5 py-0.5 rounded font-medium" style={{ backgroundColor: `${winningTeam.color}20`, color: winningTeam.color }}>
                D:{p.name.split(' ')[0]}
              </span>
            ) : null;
          })}
          {Array.from(defendersLosing).map(pid => {
            const p = losingTeam.players.find(pl => pl.id === pid);
            return p ? (
              <span key={pid} className="px-1.5 py-0.5 rounded font-medium" style={{ backgroundColor: `${losingTeam.color}20`, color: losingTeam.color }}>
                D:{p.name.split(' ')[0]}
              </span>
            ) : null;
          })}
          {liberoTeamA && (
            <span className="px-1.5 py-0.5 rounded font-medium" style={{ backgroundColor: `${winningTeam.color}20`, color: winningTeam.color }}>
              L:{winningTeam.players.find(p => p.id === liberoTeamA)?.name.split(' ')[0]}
            </span>
          )}
          {liberoTeamB && (
            <span className="px-1.5 py-0.5 rounded font-medium" style={{ backgroundColor: `${losingTeam.color}20`, color: losingTeam.color }}>
              L:{losingTeam.players.find(p => p.id === liberoTeamB)?.name.split(' ')[0]}
            </span>
          )}
          {setter && (
            <span className="px-1.5 py-0.5 rounded font-medium" style={{ backgroundColor: `${winningTeam.color}20`, color: winningTeam.color }}>
              S:{winningTeam.players.find(p => p.id === setter)?.name.split(' ')[0]}
            </span>
          )}
          {blocker && (
            <span className="px-1.5 py-0.5 rounded font-medium" style={{ backgroundColor: `${winningTeam.color}20`, color: winningTeam.color }}>
              B:{winningTeam.players.find(p => p.id === blocker)?.name.split(' ')[0]}
            </span>
          )}
          {finisher && (
            <span className="px-1.5 py-0.5 rounded font-medium" style={{ backgroundColor: `${winningTeam.color}20`, color: winningTeam.color }}>
              F:{winningTeam.players.find(p => p.id === finisher)?.name.split(' ')[0]}
            </span>
          )}
          {!hasSelections && (
            <span className="text-text-muted italic">None</span>
          )}
        </div>
      </div>
    );
  };

  // Tab content components
  const DefenderTab = () => (
    <div className="space-y-2">
      {/* Team Headers */}
      <div className="grid grid-cols-2 gap-3">
        <p className="text-xs font-bold uppercase text-center" style={{ color: winningTeam.color }}>{winningTeam.shortName}</p>
        <p className="text-xs font-bold uppercase text-center" style={{ color: losingTeam.color }}>{losingTeam.shortName}</p>
      </div>
      {/* Player Rows */}
      <div className="space-y-1">
        {winningTeam.players.map((player, index) => {
          const losingPlayer = losingTeam.players[index];
          return (
            <div key={player.id} className="grid grid-cols-2 gap-3">
              <CompactPlayerButton
                player={player}
                selected={defendersWinning.has(player.id)}
                teamColor={winningTeam.color}
                disabled={allLiberos.has(player.id)}
                onClick={() => toggleDefender(player.id, true)}
              />
              {losingPlayer && (
                <CompactPlayerButton
                  player={losingPlayer}
                  selected={defendersLosing.has(losingPlayer.id)}
                  teamColor={losingTeam.color}
                  disabled={allLiberos.has(losingPlayer.id)}
                  onClick={() => toggleDefender(losingPlayer.id, false)}
                />
              )}
            </div>
          );
        })}
      </div>
      <SelectionSummary />
    </div>
  );

  const LiberoTab = () => (
    <div className="space-y-2">
      <p className="text-xs text-text-muted text-center">Maximum 1 Libero per team</p>
      {/* Team Headers */}
      <div className="grid grid-cols-2 gap-3">
        <p className="text-xs font-bold uppercase text-center" style={{ color: winningTeam.color }}>{winningTeam.shortName}</p>
        <p className="text-xs font-bold uppercase text-center" style={{ color: losingTeam.color }}>{losingTeam.shortName}</p>
      </div>
      {/* Player Rows */}
      <div className="space-y-1">
        {winningTeam.players.map((player, index) => {
          const losingPlayer = losingTeam.players[index];
          return (
            <div key={player.id} className="grid grid-cols-2 gap-3">
              <CompactPlayerButton
                player={player}
                selected={liberoTeamA === player.id}
                teamColor={winningTeam.color}
                disabled={allDefenders.has(player.id) || (liberoTeamA !== null && liberoTeamA !== player.id)}
                onClick={() => handleLiberoSelect(player.id, true)}
              />
              {losingPlayer && (
                <CompactPlayerButton
                  player={losingPlayer}
                  selected={liberoTeamB === losingPlayer.id}
                  teamColor={losingTeam.color}
                  disabled={allDefenders.has(losingPlayer.id) || (liberoTeamB !== null && liberoTeamB !== losingPlayer.id)}
                  onClick={() => handleLiberoSelect(losingPlayer.id, false)}
                />
              )}
            </div>
          );
        })}
      </div>
      <SelectionSummary />
    </div>
  );

  const BlockerTab = () => (
    <div className="space-y-4">
      <p className="text-xs text-text-muted text-center">Winning team only - auto-assigns Finisher (F)</p>
      <div>
        <p className="text-sm font-bold uppercase mb-2" style={{ color: winningTeam.color }}>{winningTeam.name}</p>
        <div className="grid grid-cols-2 gap-2">
          {winningTeam.players.map(player => (
            <PlayerButton
              key={player.id}
              player={player}
              selected={blocker === player.id}
              teamColor={winningTeam.color}
              onClick={() => handleBlockerSelect(player.id)}
            />
          ))}
        </div>
      </div>
      {blocker && (
        <p className="text-xs text-info font-medium text-center">Blocker auto-assigns Finisher (F) to same player.</p>
      )}
      <SelectionSummary />
    </div>
  );

  const SetterFinisherTab = () => (
    <div className="space-y-2">
      <p className="text-xs text-text-muted text-center">Winning team only - {winningTeam.shortName}</p>
      {/* Column Headers */}
      <div className="grid grid-cols-2 gap-3">
        <div className="text-center">
          <span className="text-white font-bold text-sm">SETTER</span>
          {blocker && <span className="text-xs text-text-muted block">disabled (B selected)</span>}
        </div>
        <div className="text-center">
          <span className="text-white font-bold text-sm">FINISHER</span>
        </div>
      </div>
      {/* Player Rows */}
      <div className="space-y-1">
        {winningTeam.players.map(player => (
          <div key={player.id} className="grid grid-cols-2 gap-3">
            <CompactPlayerButton
              player={player}
              selected={setter === player.id}
              teamColor={winningTeam.color}
              disabled={blocker !== null || finisher === player.id}
              onClick={() => handleSetterSelect(player.id)}
            />
            <CompactPlayerButton
              player={player}
              selected={finisher === player.id}
              teamColor={winningTeam.color}
              disabled={(blocker !== null && player.id !== blocker) || setter === player.id}
              onClick={() => handleFinisherSelect(player.id)}
            />
          </div>
        ))}
      </div>
      {setterNeedsFinisher && (
        <p className="text-xs text-warning font-medium text-center">Setter requires a Finisher (F) to be assigned.</p>
      )}
      <SelectionSummary />
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/80 z-50">
      <div className="absolute bottom-0 left-0 right-0 bg-surface border-t border-border rounded-t-2xl max-h-[90vh] flex flex-col animate-slide-up">
        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="w-12 h-1 bg-border rounded-full mx-auto mb-4" />

          {/* Persistent Scoring Header */}
          <ScoringHeader
            teamA={teamA}
            teamB={teamB}
            currentSetNumber={currentSetNumber}
            matchType={matchType}
            currentScoreA={currentScoreA}
            currentScoreB={currentScoreB}
            completedSets={completedSets}
            servingTeamId={servingTeamId}
            completedTimeouts={completedTimeouts}
            compact
          />

          {/* Combined Serving Context, Rally Winner & ACE/SERVER FAULT Row */}
          <div className="mb-3 bg-surface-raised rounded-lg p-2 border border-border">
            <div className="grid grid-cols-3 gap-2 items-center">
              {/* LEFT COLUMN: Serving Team & Current Server (compact, single line) */}
              <div className="flex flex-col gap-0.5 text-xs">
                <div className="flex items-center gap-1 whitespace-nowrap">
                  <span className="text-text-muted">Serving:</span>
                  <span className="font-semibold" style={{ color: servingTeamObj?.color }}>{servingTeamObj?.shortName}</span>
                </div>
                <div className="flex items-center gap-1 whitespace-nowrap">
                  <span className="text-text-muted">Server:</span>
                  <span className="font-semibold" style={{ color: servingTeamObj?.color }}>{currentServer?.name}</span>
                </div>
              </div>

              {/* CENTER COLUMN: Rally Winner */}
              <div className="flex items-center justify-center">
                <div className="flex items-center gap-1 text-xs whitespace-nowrap">
                  <span className="text-text-muted">Winner:</span>
                  <span className="font-bold" style={{ color: winningTeam.color }}>{winningTeam.shortName}</span>
                </div>
              </div>

              {/* RIGHT COLUMN: ACE or SERVER FAULT (mutually exclusive) */}
              <div className="flex items-center justify-end">
                {isWinningTeamServing && currentServer ? (
                  /* Serving team wins - show ACE */
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={aceServe}
                      onChange={(e) => {
                        setAceServe(e.target.checked);
                        if (e.target.checked) {
                          setServerFault(false);
                        }
                      }}
                      className="w-4 h-4 rounded border-border bg-surface text-success focus:ring-success"
                    />
                    <span className="text-success font-bold text-xs whitespace-nowrap">ACE</span>
                  </label>
                ) : canShowServerFault ? (
                  /* Receiving team wins - show SERVER FAULT */
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={serverFault}
                      onChange={(e) => {
                        setServerFault(e.target.checked);
                        if (e.target.checked) {
                          setAceServe(false);
                          setDefendersWinning(new Set());
                          setDefendersLosing(new Set());
                          setLiberoTeamA(null);
                          setLiberoTeamB(null);
                          setSetter(null);
                          setBlocker(null);
                          setFinisher(null);
                        }
                      }}
                      className="w-4 h-4 rounded border-error bg-surface text-error focus:ring-error"
                    />
                    <span className="text-error font-bold text-xs whitespace-nowrap">SERVER FAULT</span>
                  </label>
                ) : null}
              </div>
            </div>
          </div>

          {/* Category Tabs */}
          {!aceServe && !serverFault && (
            <>
              {/* Tab Headers */}
              <div className="flex gap-2 mb-3 border-b border-border pb-2">
                <button
                  onClick={() => setActiveTab('D')}
                  className={`flex-1 py-2 px-3 rounded-lg font-semibold text-sm transition-all ${
                    activeTab === 'D'
                      ? 'bg-warning text-black'
                      : 'bg-surface-raised text-text-secondary hover:bg-surface'
                  }`}
                >
                  Defender (D)
                </button>
                <button
                  onClick={() => setActiveTab('L')}
                  className={`flex-1 py-2 px-3 rounded-lg font-semibold text-sm transition-all ${
                    activeTab === 'L'
                      ? 'bg-info text-black'
                      : 'bg-surface-raised text-text-secondary hover:bg-surface'
                  }`}
                >
                  Libero (L)
                </button>
                <button
                  onClick={() => setActiveTab('B')}
                  className={`flex-1 py-2 px-3 rounded-lg font-semibold text-sm transition-all ${
                    activeTab === 'B'
                      ? 'bg-team-b text-white'
                      : 'bg-surface-raised text-text-secondary hover:bg-surface'
                  }`}
                >
                  Blocker (B)
                </button>
                <button
                  onClick={() => setActiveTab('SF')}
                  className={`flex-1 py-2 px-3 rounded-lg font-semibold text-sm transition-all ${
                    activeTab === 'SF'
                      ? 'bg-team-a text-white'
                      : 'bg-surface-raised text-text-secondary hover:bg-surface'
                  }`}
                >
                  S/F
                </button>
              </div>

              {/* Tab Content */}
              <div className="mb-4">
                {activeTab === 'D' && <DefenderTab />}
                {activeTab === 'L' && <LiberoTab />}
                {activeTab === 'B' && <BlockerTab />}
                {activeTab === 'SF' && <SetterFinisherTab />}
              </div>
            </>
          )}

          {error && (
            <div className="bg-error/20 border border-error rounded-lg p-3 mb-4">
              <p className="text-error text-sm text-center">{error}</p>
            </div>
          )}
        </div>

        {/* Sticky Footer - Confirm/Cancel */}
        <div className="flex-shrink-0 p-4 bg-surface border-t border-border pb-safe">
          <div className="flex gap-3">
            <button
              onClick={onCancel}
              disabled={submitting}
              className="flex-1 py-3 px-4 rounded-lg font-semibold text-sm bg-surface border border-border text-text-secondary transition-all active:scale-[0.98] disabled:opacity-50"
            >
              CANCEL
            </button>
            <button
              onClick={handleConfirm}
              disabled={submitting || !!setterNeedsFinisher}
              className="flex-1 py-3 px-4 rounded-lg font-bold text-sm bg-success text-white transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  SAVING...
                </span>
              ) : 'CONFIRM'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
